import React, { useEffect, useMemo, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useTheme } from "styled-components"
import { REQ_API } from "../managers/Api"
import { Panel } from "../components/Panel"
import { Box } from "../components/box/Box"
import { Typo } from "../components/text/Typo"
import { Spacer } from "../components/Spacer"
import { Divider } from "../components/Divider"
import { SessionsPanel } from "./SessionsPanel"
import { ISessionGetFilter } from "../managers/Sessions"

import dayjs from "dayjs"

// ─── Types ───────────────────────────────────────────────────────────────────

interface IStatRow {
    date: string
    country: string
    institution: string
    total_requests: number
    statuses: Record<string, number>
    ratio_percent: string | number
}

interface IDateAgg {
    date: string
    total: number
    completed: number
    problems: number
    statuses: Record<string, number>
}

interface ISeries {
    name: string
    color: string
    values: (number | null)[]
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PAYMENT_SYSTEMS = ["ap", "gp", "ob", "yapily", "yapily_predefined", "yapily_repay"]
const PS_EXPAND: Record<string, string[]> = {
    ob: ["yapily", "yapily_predefined"],
}
const PS_LABELS: Record<string, string> = {
    ap: "Apple Pay",
    gp: "Google Pay",
    ob: "OB",
    yapily: "OB/Yapily",
    yapily_predefined: "OB/Predefined",
    yapily_repay: "OB/Repay",
}


const LINE_COLORS = [
    "rgb(0,221,255)", "#6cc38a", "#c51e7a",
    "#caab64", "#7540a5", "#ff9800", "#3cb4ff",
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Wilson score lower bound (95% CI) — honest ranking that penalises low sample sizes
function wilsonLower(successes: number, n: number): number {
    if (n === 0) return 0
    const z = 1.96
    const p = successes / n
    return (p + z * z / (2 * n) - z * Math.sqrt(p * (1 - p) / n + z * z / (4 * n * n))) / (1 + z * z / n)
}

function todayStr() { return dayjs().format("YYYY-MM-DD") }
function monthStartStr() { return dayjs().startOf("month").format("YYYY-MM-DD") }

function aggregateByDate(rows: IStatRow[]): IDateAgg[] {
    const map = new Map<string, IDateAgg>()
    for (const row of rows) {
        let agg = map.get(row.date)
        if (!agg) {
            agg = { date: row.date, total: 0, completed: 0, problems: 0, statuses: {} }
            map.set(row.date, agg)
        }
        agg.total += row.total_requests
        agg.completed += row.statuses["completed"] ?? 0
        agg.problems += (row.statuses["fail"] ?? 0) + (row.statuses["error"] ?? 0)
        for (const [st, cnt] of Object.entries(row.statuses))
            agg.statuses[st] = (agg.statuses[st] ?? 0) + cnt
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
}


// ─── SVG Line Chart ──────────────────────────────────────────────────────────

function smoothBezierAreaPath(pts: [number, number][], bottomY: number, yMin?: number, yMax?: number): string {
    if (pts.length < 2) return ""
    const line = smoothBezierPath(pts, yMin, yMax)
    return line + ` L ${pts[pts.length - 1][0]},${bottomY} L ${pts[0][0]},${bottomY} Z`
}

function smoothBezierPath(pts: [number, number][], yMin?: number, yMax?: number): string {
    if (pts.length < 2) return ""
    const cy = (y: number) => yMin !== undefined && yMax !== undefined
        ? Math.min(yMax, Math.max(yMin, y)) : y
    let d = `M ${pts[0][0]},${pts[0][1]}`
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(i - 1, 0)]
        const p1 = pts[i]
        const p2 = pts[i + 1]
        const p3 = pts[Math.min(i + 2, pts.length - 1)]
        const cp1x = p1[0] + (p2[0] - p0[0]) / 6
        const cp1y = cy(p1[1] + (p2[1] - p0[1]) / 6)
        const cp2x = p2[0] - (p3[0] - p1[0]) / 6
        const cp2y = cy(p2[1] - (p3[1] - p1[1]) / 6)
        d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0]},${p2[1]}`
    }
    return d
}

const SvgLineChart = ({ series, labels, onClickPoint, autoZoom }: {
    series: ISeries[]
    labels: string[]
    onClickPoint?: (label: string) => void
    autoZoom?: boolean
}) => {
    const [hoverIdx, setHoverIdx] = useState<number | null>(null)
    const [lastHi,  setLastHi]   = useState(0)

    const svgRef   = useRef<SVGSVGElement>(null)
    const [renderW, setRenderW] = useState(600)
    useEffect(() => {
        const el = svgRef.current
        if (!el) return
        const update = () => { const w = el.getBoundingClientRect().width; if (w > 0) setRenderW(w) }
        update()
        const ro = new ResizeObserver(update)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // animated display values — interpolate from prev to next on data change
    // nulls stay null (no data), numbers animate
    const toNum = (v: number | null) => v ?? 0
    const [dispVals, setDispVals] = useState<(number | null)[][]>(() => series.map(s => [...s.values]))
    const rafRef    = useRef<number | null>(null)
    const fromRef   = useRef<(number | null)[][]>(series.map(s => [...s.values]))
    const mountedRef = useRef(false)

    const structKey = series.map(s => s.values.length).join(",")
    const dataKey   = series.map(s => s.values.reduce((a: number, b) => a + toNum(b), 0)).join(",")

    useEffect(() => {
        const to = series.map(s => [...s.values])
        const from = fromRef.current
        const sameStructure = from.length === to.length &&
            from.every((f, i) => f.length === to[i].length)

        if (!mountedRef.current || !sameStructure) {
            mountedRef.current = true
            fromRef.current = to
            setDispVals(to)
            return
        }

        const start = performance.now()
        const dur = 480

        const tick = (now: number) => {
            const raw = Math.min((now - start) / dur, 1)
            const t = raw < 0.5 ? 4 * raw * raw * raw : 1 - (-2 * raw + 2) ** 3 / 2
            setDispVals(to.map((toVals, si) =>
                toVals.map((v, vi) =>
                    v === null ? null : toNum(from[si][vi]) + (v - toNum(from[si][vi])) * t
                )
            ))
            if (raw < 1) rafRef.current = requestAnimationFrame(tick)
            else fromRef.current = to
        }

        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        rafRef.current = requestAnimationFrame(tick)
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structKey, dataKey])

    const W = 600, H = 180
    const PL = 50, PR = 12, PT = 10, PB = 28
    const cW = W - PL - PR, cH = H - PT - PB
    const N = labels.length
    if (N === 0) return null
    // px(n) → viewBox units that render as exactly n screen pixels regardless of SVG scale
    const s = W / Math.max(renderW, 1)
    const px = (n: number) => n * s

    const isHourly = labels[0].length === 5
    const xLabel = (d: string) => isHourly ? d : d.substring(8)
    const tipDate = (d: string) => isHourly ? d : dayjs(d).format("D MMM YYYY")

    const allNonNull = dispVals.flatMap(v => v.filter(x => x !== null)) as number[]
    const dataMin = allNonNull.length > 0 ? Math.min(...allNonNull) : 0
    const dataMax = allNonNull.length > 0 ? Math.max(...allNonNull) : 1
    const pad = Math.max((dataMax - dataMin) * 0.18, 1)
    const effMin = autoZoom ? Math.max(0, dataMin - pad) : 0
    const effMax = autoZoom ? dataMax + pad : Math.max(dataMax, 1)
    const xOf = (i: number) => PL + (N === 1 ? cW / 2 : (i / (N - 1)) * cW)
    const yOf = (v: number) => PT + cH - ((v - effMin) / Math.max(effMax - effMin, 1)) * cH

    // draw-animation key: only changes on structure change
    const drawKey = structKey

    const nearestIdx = (e: React.MouseEvent<SVGSVGElement>) => {
        const rect = e.currentTarget.getBoundingClientRect()
        const svgX = (e.clientX - rect.left) * (W / rect.width)
        let nearest = 0, minDist = Infinity
        for (let i = 0; i < N; i++) {
            const d = Math.abs(xOf(i) - svgX)
            if (d < minDist) { minDist = d; nearest = i }
        }
        return nearest
    }

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const idx = nearestIdx(e)
        setHoverIdx(idx)
        setLastHi(idx)
    }

    const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
        if (!onClickPoint) return
        onClickPoint(labels[nearestIdx(e)])
    }

    const hx = hoverIdx !== null ? xOf(hoverIdx) : xOf(lastHi)

    const buildTipLines = (hi: number) => {
        const safeHi = Math.min(hi, N - 1)
        const get = (n: string) => series.find(s => s.name === n)?.values[safeHi] ?? 0
        const completed = get("Completed"), fails = get("Fails"), expired = get("Expired")
        const denom = completed + fails + expired
        const conv = denom > 0 ? ((completed / denom) * 100).toFixed(1) + "%" : "—"
        const convColor = denom > 0
            ? (completed / denom >= 0.8 ? "#6cc38a" : completed / denom >= 0.6 ? "#caab64" : "#c51e7a")
            : "rgba(255,255,255,.3)"
        return [
            { name: tipDate(labels[safeHi] ?? ""), value: "", valueColor: "" },
            ...series.map(s => ({ name: s.name, value: (s.values[safeHi] ?? 0).toLocaleString(), valueColor: s.color })),
            { name: "Conversion", value: conv, valueColor: convColor },
        ]
    }
    const tipLines = buildTipLines(lastHi)
    const TW_px = 200, TH_px = tipLines.length * 22 + 14
    const TW = px(TW_px)
    const TX = hx + TW + px(16) > W ? hx - TW - px(8) : hx + px(12)

    return (
        <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            style={{ width: "100%", height: "auto", display: "block", overflow: "visible", cursor: onClickPoint ? "pointer" : "crosshair" }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverIdx(null)}
            onClick={handleClick}
        >
            <style>{`
                @keyframes _chartDraw {
                    from { stroke-dashoffset: 2000; opacity: 0; }
                    to   { stroke-dashoffset: 0;    opacity: 0.9; }
                }
            `}</style>

            {/* Y grid */}
            {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
                const y = PT + cH - t * cH
                return (
                    <g key={i}>
                        <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="rgba(255,255,255,.06)" strokeWidth={px(1)} />
                        <text x={PL - px(4)} y={y + px(4)} textAnchor="end" fill="rgba(255,255,255,.22)" fontSize={px(10)}>
                            {Math.round(effMin + t * (effMax - effMin)).toLocaleString()}
                        </text>
                    </g>
                )
            })}

            {/* X labels — show all that fit based on actual pixel spacing */}
            {(() => {
                const spacingPx = N > 1 ? renderW * (cW / W) / (N - 1) : renderW
                const step = spacingPx >= 22 ? 1 : spacingPx >= 14 ? 2 : 3
                return labels.map((d, i) => {
                    if (i % step !== 0) return null
                    return (
                        <text key={i} x={xOf(i)} y={H - px(4)} textAnchor="middle" fill="rgba(255,255,255,.22)" fontSize={px(10)}>
                            {xLabel(d)}
                        </text>
                    )
                })
            })()}

            {/* Lines + dots — drawKey remounts only on structure change → restarts draw anim */}
            {series.map((s, si) => {
                const validPts = (dispVals[si] ?? []).flatMap((v, i) => v !== null ? [[xOf(i), yOf(v)] as [number, number]] : [])
                return (
                <g key={`${si}-${drawKey}`}>
                    <path
                        d={smoothBezierAreaPath(validPts, PT + cH, PT, PT + cH)}
                        fill={s.color} stroke="none" opacity={0.03}
                        style={{ pointerEvents: "none" }}
                    />
                    <path
                        d={smoothBezierPath(validPts, PT, PT + cH)}
                        fill="none" stroke={s.color} strokeWidth={px(1.2)}
                        style={{
                            strokeDasharray: 2000,
                            animation: `_chartDraw ${0.55 + si * 0.07}s ${si * 0.05}s ease-out forwards`,
                        }}
                    />
                    {(dispVals[si] ?? []).map((v, i) => v === null ? null : (
                        <circle key={i} cx={xOf(i)} cy={yOf(v)} r={px(2)} fill={s.color}
                            opacity={hoverIdx === i ? 0 : 0.65} />
                    ))}
                </g>
            )})}


            {/* Crosshair */}
            {hoverIdx !== null && (
                <line x1={hx} y1={PT} x2={hx} y2={PT + cH}
                    stroke="rgba(255,255,255,.3)" strokeWidth={px(1)} strokeDasharray={`${px(3)} ${px(3)}`}
                    style={{ pointerEvents: "none" }} />
            )}
            {hoverIdx !== null && series.map((s, si) => {
                const v = dispVals[si]?.[hoverIdx]
                if (v === null || v === undefined) return null
                return <circle key={si} cx={hx} cy={yOf(v)}
                    r={px(4)} fill={s.color} stroke="rgba(0,0,0,.6)" strokeWidth={px(1.2)}
                    style={{ pointerEvents: "none" }} />
            })}

            {/* Tooltip — fixed screen size via inverse scale transform */}
            <g style={{ pointerEvents: "none", opacity: hoverIdx !== null ? 1 : 0, transition: "opacity 0.15s ease-out" }}
                transform={`translate(${TX},${PT}) scale(${s})`}>
                <rect x={0} y={0} width={TW_px} height={TH_px} rx={3}
                    fill="rgba(8,10,20,.96)" stroke="rgba(255,255,255,.1)" strokeWidth={0.5} />
                {tipLines.map((row, i) => (
                    <text key={i} x={9} y={18 + i * 22} fontSize={i === 0 ? 16 : 18}>
                        {i === 0
                            ? <tspan fill="rgba(255,255,255,.9)">{row.name}</tspan>
                            : <><tspan fill="rgba(255,255,255,.35)">{row.name}: </tspan><tspan fill={row.valueColor} fontWeight="600">{row.value}</tspan></>
                        }
                    </text>
                ))}
            </g>
        </svg>
    )
}

const ChartLegend = ({ items }: { items: { name: string; color: string }[] }) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", marginTop: "6px" }}>
        {items.map((item, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box sx={{ width: "22px", height: "3px", borderRadius: "2px", backgroundColor: item.color }} />
                <Typo variant="small" color="rgba(255,255,255,.45)">{item.name}</Typo>
            </Box>
        ))}
    </Box>
)

// ─── Conversion helpers ──────────────────────────────────────────────────────

function convColor(pct: number): string {
    if (pct >= 80) return "#6cc38a"
    if (pct >= 60) return "#caab64"
    return "#c51e7a"
}

interface IGroupStat { name: string; total: number; completed: number; fails: number; expired: number; conv: number; wilson: number }

const ConvRankList = ({ title, items, nameUpper }: { title: string; items: IGroupStat[]; nameUpper?: boolean }) => {
    const [expanded, setExpanded] = useState(false)
    const [mode, setMode] = useState<"best"|"worst">("best")
    const TOP = 15

    const sorted = mode === "best"
        ? [...items].sort((a, b) => b.wilson - a.wilson)
        : [...items].sort((a, b) => a.conv - b.conv)
    const visible = expanded ? sorted : sorted.slice(0, TOP)

    return (
        <Box vflex sx={{ gap: "4px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <Typo variant="small" uppercase color="rgba(255,255,255,.4)" sx={{ letterSpacing: "0.08em", flexGrow: 1 }}>{title}</Typo>
                {(["best","worst"] as const).map(m => (
                    <Box key={m} onClick={() => { setMode(m); setExpanded(false) }} sx={{
                        padding: "2px 8px", borderRadius: "20px", cursor: "pointer", userSelect: "none",
                        border: mode === m ? "1px solid rgba(60,180,255,.7)" : "1px solid rgba(255,255,255,.1)",
                        backgroundColor: mode === m ? "rgba(60,180,255,.15)" : "transparent",
                    }}>
                        <Typo variant="small" sx={{ fontSize: "10px" }} color={mode === m ? "rgb(120,210,255)" : "rgba(255,255,255,.3)"}>
                            {m === "best" ? "▲ BEST" : "▼ WORST"}
                        </Typo>
                    </Box>
                ))}
            </Box>
            {visible.map((g, i) => (
                <Box key={g.name} sx={{ display: "grid", gridTemplateColumns: "20px 1fr 52px 60px", alignItems: "center", gap: "8px" }}>
                    <Typo variant="small" color="rgba(255,255,255,.2)" align="right" sx={{ fontSize: "9px" }}>{i + 1}</Typo>
                    <Box sx={{ position: "relative", height: "20px", borderRadius: "3px", backgroundColor: "rgba(0,0,0,.25)", overflow: "hidden" }}>
                        <Box sx={{
                            position: "absolute", top: 0, left: 0, height: "100%",
                            width: `${g.conv}%`,
                            backgroundColor: convColor(g.conv),
                            opacity: 0.25, borderRadius: "3px",
                        }} />
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 8px" }}>
                            <Typo variant="small" sx={{ fontSize: "10px" }} color="rgba(255,255,255,.8)">
                                {nameUpper ? g.name.toUpperCase() : g.name}
                            </Typo>
                            <Typo variant="small" sx={{ fontSize: "9px" }} color="rgba(255,255,255,.25)">
                                {g.total.toLocaleString()}
                            </Typo>
                        </Box>
                    </Box>
                    <Typo variant="small" sx={{ fontSize: "11px" }} color={convColor(g.conv)} align="right">
                        {g.conv.toFixed(1)}%
                    </Typo>
                    <Box title="Wilson lower bound — honest rank accounting for volume" sx={{ textAlign: "right" }}>
                        <Typo variant="small" sx={{ fontSize: "9px" }} color="rgba(255,255,255,.2)">
                            ≥{g.wilson.toFixed(1)}%
                        </Typo>
                    </Box>
                </Box>
            ))}
            {sorted.length > TOP && (
                <Box onClick={() => setExpanded(e => !e)} sx={{ cursor: "pointer", paddingTop: "4px" }}>
                    <Typo variant="small" color="rgba(255,255,255,.25)" sx={{ fontSize: "10px" }}>
                        {expanded ? "LESS" : `+${sorted.length - TOP} MORE`}
                    </Typo>
                </Box>
            )}
        </Box>
    )
}

// ─── Select Chips ────────────────────────────────────────────────────────────

const SelectChips = ({ label, options, selected, onChange, top = 0, radio = false }: {
    label: string
    options: string[]
    selected: string[]
    onChange: (v: string[]) => void
    top?: number
    radio?: boolean
}) => {
    const [expanded, setExpanded] = useState(false)
    const toggle = (opt: string) => {
        if (radio)
            onChange(selected.includes(opt) ? [] : [opt])
        else
            onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt])
    }

    const hasMore = top > 0 && options.length > top
    const visible = hasMore && !expanded ? options.slice(0, top) : options
    // always show selected chips even if hidden
    const extraSelected = hasMore && !expanded
        ? selected.filter(s => !visible.includes(s))
        : []

    return (
        <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <Typo variant="small" uppercase
                sx={{ minWidth: "96px", flexShrink: 0, paddingTop: "5px", fontSize: "10px", letterSpacing: "0.1em", color: "rgba(255,255,255,.55)" }}>
                {label}
            </Typo>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", flex: 1 }}>
                {selected.length > 0 && (
                    <Box onClick={() => onChange([])} sx={{
                        padding: "4px 13px", borderRadius: "20px", cursor: "pointer", userSelect: "none",
                        border: "1px solid rgba(255,255,255,.3)", backgroundColor: "rgba(255,255,255,.08)",
                    }}>
                        <Typo variant="small" sx={{ fontSize: "11px" }} color="rgba(255,255,255,.7)">ALL</Typo>
                    </Box>
                )}
                {visible.map(opt => {
                    const active = selected.includes(opt)
                    const dim = selected.length > 0 && !active
                    return (
                        <Box key={opt} onClick={() => toggle(opt)} sx={{
                            padding: "4px 13px", borderRadius: "20px", cursor: "pointer", userSelect: "none",
                            border: active ? "1px solid rgba(60,180,255,.85)" : "1px solid rgba(255,255,255,.18)",
                            backgroundColor: active ? "rgba(60,180,255,.22)" : "rgba(255,255,255,.03)",
                            opacity: dim ? 0.35 : 1,
                            transition: "opacity .15s",
                        }}>
                            <Typo variant="small" sx={{ fontSize: "11px" }}
                                color={active ? "rgb(120,210,255)" : "rgba(255,255,255,.6)"}>
                                {opt}
                            </Typo>
                        </Box>
                    )
                })}
                {extraSelected.map(opt => (
                    <Box key={opt} onClick={() => toggle(opt)} sx={{
                        padding: "4px 13px", borderRadius: "20px", cursor: "pointer", userSelect: "none",
                        border: "1px solid rgba(60,180,255,.85)",
                        backgroundColor: "rgba(60,180,255,.22)",
                    }}>
                        <Typo variant="small" sx={{ fontSize: "11px" }} color="rgb(120,210,255)">{opt}</Typo>
                    </Box>
                ))}
                {hasMore && (
                    <Box onClick={() => setExpanded(e => !e)} sx={{
                        padding: "4px 13px", borderRadius: "20px", cursor: "pointer", userSelect: "none",
                        border: "1px solid rgba(255,255,255,.12)", backgroundColor: "transparent",
                    }}>
                        <Typo variant="small" sx={{ fontSize: "11px" }} color="rgba(255,255,255,.35)">
                            {expanded ? "LESS" : `+${options.length - top} MORE`}
                        </Typo>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

// ─── Main Panel ──────────────────────────────────────────────────────────────

export const NewStatPanel = () => {
    const theme = useTheme()
    const [from, setFrom] = useState(monthStartStr())
    const [to, setTo] = useState(todayStr())
    const [selectedPS, setSelectedPS] = useState<string>("")
    const [merchantInput, setMerchantInput] = useState<string>("")
    const [merchantIds,   setMerchantIds]   = useState<string[]>([])
    const [rows, setRows] = useState<IStatRow[]>([])
    const [busy, setBusy] = useState(false)
    const [selInst,    setSelInst]    = useState<string[]>([])
    const [selCountry, setSelCountry] = useState<string[]>([])

    const load = () => {
        setBusy(true)
        REQ_API.request({
            endpoint: "stat",
            data: {
                from,
                to,
                ...(selectedPS    && { payment_system: PS_EXPAND[selectedPS] ?? [selectedPS] }),
                ...(merchantIds.length > 0 && { merchant_id: merchantIds }),
            },
        }).then(res => {
            const data = (res as any)?.data
            if (Array.isArray(data)) {
                const isHourly = from === to
                setRows(data.map((r: any) => ({
                    ...r,
                    date: isHourly
                        ? String(r.hour).padStart(2, "0") + ":00"
                        : dayjs(r.date).format("YYYY-MM-DD"),
                })))
            }
        }).finally(() => setBusy(false))
    }
    const loadRef = useRef(load)
    useEffect(() => { loadRef.current = load })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load() }, [from, to, selectedPS, merchantIds])

    // Filter options (always from full rows so chips don't disappear)
    // Institution options: filtered by selected countries (so only relevant institutions appear)
    const allInstOptions = useMemo(() => {
        const source = selCountry.length > 0 ? rows.filter(r => selCountry.includes(r.country)) : rows
        const totals = new Map<string, number>()
        for (const r of source) if (r.institution) totals.set(r.institution, (totals.get(r.institution) ?? 0) + r.total_requests)
        // keep any already-selected institutions even if filtered out (to allow deselecting)
        for (const s of selInst) if (!totals.has(s)) totals.set(s, 0)
        return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).map(e => e[0])
    }, [rows, selCountry, selInst])

    // Country options: filtered by selected institutions (so only relevant countries appear)
    const allCountryOptions = useMemo(() => {
        const source = selInst.length > 0 ? rows.filter(r => selInst.includes(r.institution)) : rows
        const totals = new Map<string, number>()
        for (const r of source) if (r.country) totals.set(r.country, (totals.get(r.country) ?? 0) + r.total_requests)
        // keep any already-selected countries even if filtered out (to allow deselecting)
        for (const s of selCountry) if (!totals.has(s)) totals.set(s, 0)
        return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).slice(0, 30).map(e => e[0])
    }, [rows, selInst, selCountry])

    // Filtered rows → drives both the chart and the stat blocks
    const filteredRows = useMemo(() => {
        let r = rows
        if (selInst.length > 0)    r = r.filter(row => selInst.includes(row.institution))
        if (selCountry.length > 0) r = r.filter(row => selCountry.includes(row.country))
        return r
    }, [rows, selInst, selCountry])

    const aggByDate = aggregateByDate(filteredRows)
    const dates = aggByDate.map(d => d.date)


    // fails = error + fail  (expired is separate)
    // problems = everything except completed, fails, expired, created, processing, pending
    const FAIL_ST   = ["error", "fail"]
    const PROB_EXCL = new Set(["created", "processing", "completed", "pending", "error", "fail", "expired"])

    const failsByDay    = aggByDate.map(d => FAIL_ST.reduce((s, st) => s + (d.statuses[st] ?? 0), 0))
    const expiredByDay  = aggByDate.map(d => d.statuses["expired"] ?? 0)
    const problemsByDay = aggByDate.map(d =>
        Object.entries(d.statuses).filter(([st]) => !PROB_EXCL.has(st)).reduce((s, [, c]) => s + c, 0))

    const mainSeries: ISeries[] = [
        { name: "Total",     color: LINE_COLORS[0],       values: aggByDate.map(d => d.total) },
        { name: "Completed", color: "#6cc38a",             values: aggByDate.map(d => d.completed) },
        { name: "Fails",     color: "#c51e7a",             values: failsByDay },
        { name: "Expired",   color: "rgb(117,64,165)",     values: expiredByDay },
        { name: "Problems",  color: "#caab64",             values: problemsByDay },
    ]

    const totalReq     = aggByDate.reduce((s, d) => s + d.total, 0)
    const totalCmp     = aggByDate.reduce((s, d) => s + d.completed, 0)
    const totalFails   = failsByDay.reduce((s, v) => s + v, 0)
    const totalExpired = expiredByDay.reduce((s, v) => s + v, 0)
    const totalProbs   = problemsByDay.reduce((s, v) => s + v, 0)

    // conv1 = completed / (completed + fails + expired)  — без problems
    // conv2 = completed / (completed + fails + expired + problems)  — с problems
    const conv1Denom = totalCmp + totalFails + totalExpired
    const conv2Denom = totalCmp + totalFails + totalExpired + totalProbs
    const conv1 = conv1Denom > 0 ? ((totalCmp / conv1Denom) * 100).toFixed(1) : "0"
    const conv2 = conv2Denom > 0 ? ((totalCmp / conv2Denom) * 100).toFixed(1) : "0"

    // Per-institution and per-country conversion stats (from filtered rows)
    // Sorted by Wilson lower bound so volume is factored in honestly
    const convByInst = useMemo(() => {
        const map = new Map<string, IGroupStat>()
        for (const row of filteredRows) {
            if (!row.institution) continue
            let g = map.get(row.institution)
            if (!g) { g = { name: row.institution, total: 0, completed: 0, fails: 0, expired: 0, conv: 0, wilson: 0 }; map.set(row.institution, g) }
            g.total     += row.total_requests
            g.completed += row.statuses["completed"] ?? 0
            g.fails     += (row.statuses["fail"] ?? 0) + (row.statuses["error"] ?? 0)
            g.expired   += row.statuses["expired"] ?? 0
        }
        for (const g of map.values()) {
            const d = g.completed + g.fails + g.expired
            g.conv   = d > 0 ? (g.completed / d) * 100 : 0
            g.wilson = wilsonLower(g.completed, d) * 100
        }
        return Array.from(map.values()).filter(g => g.completed + g.fails + g.expired >= 3).sort((a, b) => b.wilson - a.wilson)
    }, [filteredRows])

    const convByCountry = useMemo(() => {
        const map = new Map<string, IGroupStat>()
        for (const row of filteredRows) {
            if (!row.country) continue
            let g = map.get(row.country)
            if (!g) { g = { name: row.country, total: 0, completed: 0, fails: 0, expired: 0, conv: 0, wilson: 0 }; map.set(row.country, g) }
            g.total     += row.total_requests
            g.completed += row.statuses["completed"] ?? 0
            g.fails     += (row.statuses["fail"] ?? 0) + (row.statuses["error"] ?? 0)
            g.expired   += row.statuses["expired"] ?? 0
        }
        for (const g of map.values()) {
            const d = g.completed + g.fails + g.expired
            g.conv   = d > 0 ? (g.completed / d) * 100 : 0
            g.wilson = wilsonLower(g.completed, d) * 100
        }
        return Array.from(map.values()).filter(g => g.completed + g.fails + g.expired >= 3).sort((a, b) => b.wilson - a.wilson)
    }, [filteredRows])

    const [preset, setPreset] = useState<"today" | "month" | null>("month")
    const [drillDown, setDrillDown] = useState<ISessionGetFilter | null>(null)
    const [autoPoll, setAutoPoll] = useState(false)
    const [countdown, setCountdown] = useState(60)

    useEffect(() => {
        if (!autoPoll) { setCountdown(60); return }
        const today = todayStr()
        setFrom(today); setTo(today); setPreset("today")
        setCountdown(60)
        let cnt = 60
        const id = setInterval(() => {
            cnt--
            if (cnt <= 0) {
                cnt = 60
                setFrom(todayStr()); setTo(todayStr())
                loadRef.current()
            }
            setCountdown(cnt)
        }, 1000)
        return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPoll])

    const buildDrillFilter = (label: string): ISessionGetFilter => {
        const isHourly = from === to
        return {
            from: isHourly ? from : label,
            to:   isHourly ? from : label,
            ...(selectedPS && { payment_system: PS_EXPAND[selectedPS] ?? [selectedPS] }),
            ...(merchantIds.length > 0 && { merchant_id: merchantIds.map(Number).filter(Boolean) }),
            ...(merchantIds.length > 0 && { search: merchantIds.join(", ") }),
            ...(selCountry.length === 1 && { country: selCountry[0] }),
            ...(selInst.length === 1    && { institution: selInst[0] }),
        }
    }

    const applyPreset = (p: "today" | "month") => {
        if (p === "today" && preset === "today") {
            setFrom(monthStartStr()); setTo(todayStr()); setPreset("month")
        } else if (p === "today") {
            setFrom(todayStr()); setTo(todayStr()); setPreset("today")
        } else {
            setFrom(monthStartStr()); setTo(todayStr()); setPreset("month")
        }
    }

    const navDay = (delta: number) => {
        const d = dayjs(from).add(delta, "day").format("YYYY-MM-DD")
        setFrom(d); setTo(d)
    }

    const togglePS = (ps: string) =>
        setSelectedPS(prev => prev === ps ? "" : ps)

    const inputStyle: React.CSSProperties = {
        background: "rgba(0,0,0,.3)",
        color: "white",
        border: "1px solid rgba(255,255,255,.12)",
        borderRadius: "4px",
        padding: "5px 9px",
        fontSize: "12px",
        outline: "none",
    }

    return (
        <Panel label={`ANALYTICS / ${from.substring(0, 7).replace("-", ".")}`} busy={busy}>
            <style>{`
                @keyframes _autoPulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
                @keyframes _cdFade { from{opacity:0.4;transform:scale(.92)} to{opacity:1;transform:scale(1)} }
            `}</style>

            {/* ── Filters ─────────────────────────────────────────────── */}
            <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>

                {/* Date / Month picker — switches based on mode */}
                {preset === "today" ? (
                    <Box vflex sx={{ gap: "4px" }}>
                        <Typo variant="small" color="rgba(255,255,255,.35)">DATE</Typo>
                        <input type="date" value={from} style={inputStyle}
                            onChange={e => {
                                if (!e.target.value) return
                                setFrom(e.target.value)
                                setTo(e.target.value)
                            }} />
                    </Box>
                ) : (
                    <Box vflex sx={{ gap: "4px" }}>
                        <Typo variant="small" color="rgba(255,255,255,.35)">MONTH</Typo>
                        <input type="month" value={from.substring(0, 7)} style={inputStyle}
                            onChange={e => {
                                const m = e.target.value
                                if (!m) return
                                const start = m + "-01"
                                const lastDay = dayjs(start).endOf("month").format("YYYY-MM-DD")
                                const today = todayStr()
                                setFrom(start)
                                setTo(lastDay > today ? today : lastDay)
                                setPreset(null)
                            }} />
                    </Box>
                )}

                {/* Auto-poll */}
                <Box onClick={() => setAutoPoll(p => !p)} sx={{
                    height: "28px", boxSizing: "border-box",
                    padding: "0 11px", borderRadius: "4px", cursor: "pointer", userSelect: "none",
                    display: "flex", alignItems: "center", gap: "7px",
                    border: autoPoll ? "1px solid rgba(106,220,120,.7)" : "1px solid rgba(255,255,255,.12)",
                    backgroundColor: autoPoll ? "rgba(106,220,120,.12)" : "transparent",
                    alignSelf: "flex-end",
                }}>
                    <Box sx={{
                        width: "8px", height: "8px", borderRadius: "50%",
                        backgroundColor: autoPoll ? "#6adc78" : "rgba(255,255,255,.2)",
                        flexShrink: 0,
                        ...(autoPoll && { animation: "_autoPulse 1.4s ease-in-out infinite" }),
                    }} />
                    <Typo variant="small" color={autoPoll ? "#6adc78" : "rgba(255,255,255,.45)"}>
                        {autoPoll
                            ? <span key={countdown} style={{ animation: "_cdFade .35s ease-out" }}>AUTO {countdown}s</span>
                            : "AUTO"
                        }
                    </Typo>
                </Box>

                {/* TODAY preset */}
                <Box onClick={() => { if (!autoPoll) applyPreset("today") }} sx={{
                    height: "28px", boxSizing: "border-box",
                    padding: "0 11px", borderRadius: "4px", cursor: "pointer", userSelect: "none",
                    display: "flex", alignItems: "center",
                    border: preset === "today" ? "1px solid rgba(60,180,255,.7)" : "1px solid rgba(255,255,255,.12)",
                    backgroundColor: preset === "today" ? "rgba(60,180,255,.15)" : "transparent",
                    alignSelf: "flex-end",
                }}>
                    <Typo variant="small" color={preset === "today" ? "rgb(60,180,255)" : "rgba(255,255,255,.45)"}>TODAY</Typo>
                </Box>

                {/* Prev / Next — only in today mode, hidden when autoPoll */}
                {preset === "today" && !autoPoll && (
                    <Box vflex sx={{ gap: "4px" }}>
                        <Typo variant="small" color="rgba(0,0,0,0)">.</Typo>
                        <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            <Box onClick={() => navDay(-1)} sx={{
                                padding: "5px 11px", borderRadius: "4px", cursor: "pointer", userSelect: "none",
                                border: "1px solid rgba(255,255,255,.12)",
                            }}>
                                <Typo variant="small" color="rgba(255,255,255,.5)">←</Typo>
                            </Box>
                            <Typo variant="small" color="rgba(255,255,255,.5)" sx={{ minWidth: "90px", textAlign: "center" }}>
                                {dayjs(from).format("D MMM YYYY")}
                            </Typo>
                            <Box onClick={() => navDay(1)} sx={{
                                padding: "5px 11px", borderRadius: "4px", cursor: "pointer", userSelect: "none",
                                border: "1px solid rgba(255,255,255,.12)",
                            }}>
                                <Typo variant="small" color="rgba(255,255,255,.5)">→</Typo>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Merchant ID */}
                <Box vflex sx={{ gap: "4px" }}>
                    <Typo variant="small" color="rgba(255,255,255,.35)">MERCHANT ID</Typo>
                    <input
                        value={merchantInput}
                        placeholder="IDs..."
                        style={{ ...inputStyle, minWidth: "160px" }}
                        onChange={e => setMerchantInput(e.target.value)}
                        onBlur={() => {
                            const ids = merchantInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
                            setMerchantIds(ids)
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                const ids = merchantInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
                                setMerchantIds(ids)
                            }
                        }}
                    />
                </Box>

                {/* Payment systems */}
                <Box vflex sx={{ gap: "4px" }}>
                    <Typo variant="small" color="rgba(255,255,255,.35)">PAYMENT SYSTEM</Typo>
                    <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {PAYMENT_SYSTEMS.map(ps => {
                            const active = selectedPS === ps
                            return (
                                <Box key={ps} onClick={() => togglePS(ps)} sx={{
                                    padding: "5px 11px", borderRadius: "20px", cursor: "pointer", userSelect: "none",
                                    border: active ? "1px solid rgba(60,180,255,.7)" : "1px solid rgba(255,255,255,.12)",
                                    backgroundColor: active ? "rgba(60,180,255,.15)" : "transparent",
                                }}>
                                    <Typo variant="small" color={active ? "rgb(60,180,255)" : "rgba(255,255,255,.35)"}>
                                        {PS_LABELS[ps]}
                                    </Typo>
                                </Box>
                            )
                        })}
                    </Box>
                </Box>

            </Box>

            <Spacer size={theme.value(5)} />
            <Divider />
            <Spacer size={theme.value(5)} />

            {rows.length > 0 && (
                <>
                    {/* ── Summary ─────────────────────────────────────────── */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: theme.value(3) }}>
                        {[
                            { name: "TOTAL",        value: totalReq.toLocaleString(),     color: "rgba(255,255,255,.85)" },
                            { name: "COMPLETED",    value: totalCmp.toLocaleString(),     color: "#6cc38a" },
                            { name: "FAILS",        value: totalFails.toLocaleString(),   color: "#c51e7a" },
                            { name: "EXPIRED",      value: totalExpired.toLocaleString(), color: "rgb(117,64,165)" },
                            { name: "PROBLEMS",     value: totalProbs.toLocaleString(),   color: "#caab64" },
                            { name: "CONVERSION",   value: conv1 + "%", color: parseFloat(conv1) > 60 ? "#6cc38a" : "#caab64" },
                            { name: "CONVERSION 2", value: conv2 + "%", color: parseFloat(conv2) > 60 ? "#6cc38a" : "#caab64" },
                        ].map((s, i) => (
                            <Box key={i} vflex sx={{ padding: theme.values.padding, borderRadius: theme.values.borderRadius, borderBottom: "2px solid rgba(255,255,255,.07)", gap: "6px" }}>
                                <Typo variant="small" color="rgba(255,255,255,.4)" uppercase block>{s.name}:</Typo>
                                <Typo variant="big" color={s.color} block>{s.value}</Typo>
                            </Box>
                        ))}
                    </Box>

                    <Spacer size={theme.value(5)} />

                    {/* ── Requests by day ──────────────────────────────────── */}
                    <Typo variant="small" uppercase color="rgba(255,255,255,.4)" block sx={{ marginBottom: "8px", letterSpacing: "0.08em" }}>
                        REQUESTS BY DAY
                    </Typo>
                    <SvgLineChart series={mainSeries} labels={dates} onClickPoint={label => setDrillDown(buildDrillFilter(label))} />
                    <ChartLegend items={mainSeries} />

                    <Spacer size={theme.value(3)} />

                    {/* ── Chart filters ────────────────────────────────────── */}
                    <Box vflex sx={{ gap: "24px", padding: "10px 12px", backgroundColor: "rgba(0,0,0,.15)", borderRadius: theme.values.borderRadius }}>
                        <SelectChips
                            label="INSTITUTION"
                            options={allInstOptions}
                            selected={selInst}
                            onChange={setSelInst}
                            top={10}
                            radio
                        />
                        <SelectChips
                            label="COUNTRY"
                            options={allCountryOptions}
                            selected={selCountry}
                            onChange={setSelCountry}
                            radio
                        />
                    </Box>

                    <Spacer size={theme.value(5)} />
                    <Divider />
                    <Spacer size={theme.value(5)} />

                    {/* ── Conversion ranking ───────────────────────────────── */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.value(4) }}>
                        <ConvRankList title="CONVERSION BY BANK" items={convByInst} />
                        <ConvRankList title="CONVERSION BY COUNTRY" items={convByCountry} nameUpper />
                    </Box>

                    <Spacer size={theme.value(5)} />
                </>
            )}

            {rows.length === 0 && !busy && (
                <Box cflex sx={{ padding: "48px 0" }}>
                    <Typo variant="head" color="rgba(255,255,255,.2)">NO DATA</Typo>
                </Box>
            )}

            <Spacer size={theme.value(5)} />

            {/* ── Drill-down overlay ───────────────────────────────────── */}
            {drillDown && ReactDOM.createPortal(
                <Box onClick={() => setDrillDown(null)} sx={{
                    position: "absolute", top: "0px", left: "0px", width: "100vw", height: "100vh", zIndex: 100,
                    backgroundColor: "rgba(0,0,0,.65)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "24px",
                }}>
                    <Box onClick={(e: any) => e.stopPropagation()} sx={{
                        width: "100%", maxWidth: "1300px", height: "82vh",
                        backgroundColor: theme.colors.panelBgColor,
                        borderRadius: "6px",
                        boxShadow: "0 0 60px rgba(0,0,0,.95)",
                        border: "1px solid rgba(255,255,255,.08)",
                        display: "flex", flexDirection: "column",
                        overflow: "hidden",
                    }}>
                        {/* Header */}
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: "10px",
                            padding: "9px 16px", flexShrink: 0,
                            borderBottom: "1px solid rgba(255,255,255,.07)",
                            backgroundColor: "rgba(0,0,0,.25)",
                        }}>
                            <Typo variant="small" color="rgba(255,255,255,.35)" sx={{ fontSize: "10px", letterSpacing: "0.1em" }}>
                                SESSIONS FOR
                            </Typo>
                            <Typo variant="small" color="white" sx={{ fontWeight: 600 }}>
                                {from === to
                                    ? dayjs(from).format("D MMM YYYY")
                                    : dayjs(drillDown.from).format("D MMM YYYY")}
                            </Typo>
                            {drillDown.payment_system && (
                                <Typo variant="small" color="rgba(60,180,255,.8)" sx={{ fontSize: "11px" }}>
                                    · {drillDown.payment_system.join(", ")}
                                </Typo>
                            )}
                            {drillDown.country && (
                                <Typo variant="small" color="rgba(60,180,255,.8)" sx={{ fontSize: "11px" }}>
                                    · {drillDown.country.toUpperCase()}
                                </Typo>
                            )}
                            {drillDown.institution && (
                                <Typo variant="small" color="rgba(60,180,255,.8)" sx={{ fontSize: "11px" }}>
                                    · {drillDown.institution}
                                </Typo>
                            )}
                            <Spacer grow />
                            <Box onClick={() => setDrillDown(null)} sx={{
                                cursor: "pointer", padding: "3px 10px", borderRadius: "4px",
                                border: "1px solid rgba(255,255,255,.1)",
                                "&:hover": { backgroundColor: "rgba(255,255,255,.07)" },
                            }}>
                                <Typo variant="small" color="rgba(255,255,255,.4)" sx={{ fontSize: "11px" }}>✕ CLOSE</Typo>
                            </Box>
                        </Box>

                        {/* Sessions panel */}
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                            <SessionsPanel initialFilter={drillDown} />
                        </Box>
                    </Box>
                </Box>,
                document.body
            )}

        </Panel>
    )
}
